import { useEffect, useState } from "react";
import { CSSObject } from "@emotion/react";
import { SchoolClosure, SchoolModel } from "model";
import { useAuth } from "pages";
import { Colors, Icon } from "swing-components";
import { translate } from "utils/string-table";
import { v4 as uuid } from "uuid";

import { getFeatureFlags } from "~utils";

import { Footer, ProfileCard } from "../ProfileCard";
import { ClosureField, ClosureFieldRow } from "./ClosureFieldRow";
import { ClosuresListItem } from "./ClosureListItem";
import { countDays } from "./utils";

export type ClosureMap = Map<string, ClosureField>;

function createClosureMap(closures: SchoolClosure[]) {
  const sorted = closures?.sort((a, b) => (a.start < b.start ? -1 : 1));
  return new Map(sorted.map((c) => [c.id, c]));
}

type SchoolClosuresViewProps = {
  school: SchoolModel;
  onSubmit: (closures: SchoolClosure[]) => void;
};

export function SchoolClosuresView(props: SchoolClosuresViewProps) {
  const { school, onSubmit } = props;
  const { closures } = school;
  const { userInfo } = useAuth();
  const { NON_SCHOOL_DAYS_V2: isNonSchoolDaysV2Enabled } = getFeatureFlags(userInfo);

  const [isEditing, setIsEditing] = useState(false);
  const [_closures, _setClosures] = useState<ClosureMap>(createClosureMap(closures));

  const closuresArray = [..._closures.entries()].map(([, obj]) => obj);
  const daysOff = countDays(closures);

  const headerString = translate("SCHOOL_CLOSURES_DAYS_HEADER");
  // Hide school days count if non-school days v2 is enabled
  const header = isNonSchoolDaysV2Enabled ? `${headerString}` : `${headerString} (${daysOff})`;

  useEffect(() => {
    _setClosures(createClosureMap(closures));
  }, [closures]);

  if (!isEditing) {
    return (
      <ProfileCard header={header} isEditing={isEditing} onClick={() => setIsEditing(true)}>
        {/* TODO: Bring this part of the copy back later, see:
            We’ll exclude these days from all of your requests.
            https://linear.app/swing-education/issue/MKTP-750/add-back-in-copy-to-non-school-days-section
        */}
        {translate("SCHOOL_CLOSURES_DAYS_DESCRIPTION")}
        <ul css={styles.list}>
          {[..._closures.entries()].map(([id, closure]) => (
            <ClosuresListItem key={id} closure={closure} />
          ))}
        </ul>
      </ProfileCard>
    );
  }

  // remove any closure dates that have been "removed"
  const activeClosures = closuresArray.filter((closure) => !closure.isRemoved);
  const isSameData = JSON.stringify(closures) === JSON.stringify(activeClosures);
  const isFormFilledOut = closuresArray.every(({ start, end, reason }) => start && end && reason);

  return (
    <ProfileCard header={header} isEditing={isEditing} onClick={() => setIsEditing(true)}>
      {/* TODO: Bring this part of the copy back later, see:
            We’ll exclude these days from all of your requests.
            https://linear.app/swing-education/issue/MKTP-750/add-back-in-copy-to-non-school-days-section
        */}
      {translate("SCHOOL_CLOSURES_DAYS_DESCRIPTION")}
      <div css={styles.listContainer}>
        <ul css={styles.list}>
          {[..._closures.entries()].map(([key, closure]) => (
            <ClosureFieldRow
              id={key}
              key={key}
              closureField={closure}
              closures={closuresArray}
              school={school}
              setClosure={_setClosures}
            />
          ))}
        </ul>
        <div css={styles.buttonContainer}>
          <button
            css={styles.button}
            onClick={() => {
              _setClosures((prevState) => {
                const newMap = new Map(prevState);
                // FIXME: We should not create UUID on the FE since causes
                // confusion when looking at the date structure to determine
                // if this closure is synced with the API/DB.
                const id = uuid();
                // Creates a new temporary school closure field
                // All values need to be truthy in order for the admin to submit a
                // new closure
                return newMap.set(id, {
                  id: undefined,
                  start: undefined,
                  end: undefined,
                  reason: undefined,
                  isRemoved: false,
                } as unknown as SchoolClosure);
              });
            }}
          >
            <Icon name="Add" color={Colors.blue500} />
            {translate("SCHOOL_CLOSURES_ADD_DAYS_LABEL")}
          </button>
        </div>
        <Footer
          disabled={isSameData || !isFormFilledOut}
          onSubmit={() => {
            onSubmit(closuresArray);
            setIsEditing(false);
          }}
          onCancel={() => {
            _setClosures(createClosureMap(closures));
            setIsEditing(false);
          }}
        />
      </div>
    </ProfileCard>
  );
}

const styles: Record<string, CSSObject> = {
  buttonContainer: {
    display: "flex",
    placeContent: "center",
  },
  button: {
    display: "flex",
    padding: 16,
    fontSize: 16,
    color: Colors.blue500,
    fontWeight: 500,
    background: "transparent",
    lineHeight: 1.5,
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  list: {
    padding: 0,
    listStyle: "none",
  },
};
